import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FocusPreferences {
  ambient_sound: string;
  sound_volume: number;
  pomodoro_mode_enabled: boolean;
  pomodoro_focus_duration: number;
  pomodoro_break_duration: number;
}

const DEFAULT_PREFERENCES: FocusPreferences = {
  ambient_sound: 'none',
  sound_volume: 0.5,
  pomodoro_mode_enabled: false,
  pomodoro_focus_duration: 25,
  pomodoro_break_duration: 5,
};

export function useFocusPreferences(userId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['focus-preferences', userId],
    queryFn: async () => {
      if (!userId) return DEFAULT_PREFERENCES;

      const { data, error } = await supabase
        .from('user_focus_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (!data) {
        // Create default preferences
        const { data: newData, error: insertError } = await supabase
          .from('user_focus_preferences')
          .insert({
            user_id: userId,
            ...DEFAULT_PREFERENCES,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        return newData as FocusPreferences;
      }

      return data as FocusPreferences;
    },
    enabled: !!userId,
  });

  const updatePreferences = useMutation({
    mutationFn: async (updates: Partial<FocusPreferences>) => {
      if (!userId) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_focus_preferences')
        .upsert({
          user_id: userId,
          ...updates,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['focus-preferences', userId] });
    },
  });

  return {
    preferences: preferences || DEFAULT_PREFERENCES,
    isLoading,
    updatePreferences: updatePreferences.mutate,
    isUpdating: updatePreferences.isPending,
  };
}
